import type { ErrorInfo, ReactNode } from 'react';
import React, { Component } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number | boolean | null | undefined>;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  prevResetKeys?: Array<string | number | boolean | null | undefined>;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    prevResetKeys: undefined
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  public static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    const { resetKeys } = props;
    const { prevResetKeys } = state;

    // If resetKeys is provided and has changed, reset the error boundary
    if (resetKeys && prevResetKeys) {
      if (resetKeys.some((key, index) => prevResetKeys[index] !== key)) {
        return {
          hasError: false,
          error: undefined,
          errorInfo: undefined,
          prevResetKeys: resetKeys
        };
      }
    }

    // If resetKeys is provided for the first time, store them
    if (resetKeys && !prevResetKeys) {
      return { prevResetKeys: resetKeys };
    }

    return null;
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Store error info for potential debugging
    this.setState({ errorInfo });

    // Call the optional onError callback
    this.props.onError?.(error, errorInfo);

    // Log to external error reporting service if available
    // Example: Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // logErrorToService(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined
    });
  };

  private handleRefreshPage = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={3} minHeight="200px">
          <Alert
            severity="error"
            sx={{
              mb: 3,
              maxWidth: '600px',
              width: '100%'
            }}
          >
            <Typography variant="h6" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {this.state.error?.message || 'An unexpected error occurred while rendering this component.'}
            </Typography>

            {/* Show stack trace in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box sx={{ mt: 2 }}>
                <details style={{ cursor: 'pointer' }}>
                  <summary style={{ marginBottom: '8px' }}>Error Details (Development Mode)</summary>
                  <pre
                    style={{
                      fontSize: '11px',
                      whiteSpace: 'pre-wrap',
                      background: '#f5f5f5',
                      padding: '8px',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflow: 'auto'
                    }}
                  >
                    {this.state.error.stack}
                  </pre>
                </details>
              </Box>
            )}
          </Alert>

          <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleRetry} color="primary">
              Try Again
            </Button>

            <Button variant="outlined" onClick={this.handleRefreshPage} color="secondary">
              Refresh Page
            </Button>
          </Box>

          {process.env.NODE_ENV === 'development' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              This error boundary is only shown in development. In production, users would see a more user-friendly
              error message.
            </Typography>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

// Higher-order component wrapper for easier usage
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

export default ErrorBoundary;
